// React
import React, { useEffect, useState, useRef, use } from 'react';

// Packages
import cc from 'classcat';
import t from 'prop-types';
import { useRouter } from 'next/router';
import { motion, AnimatePresence } from 'framer-motion';

// Utilities
import { useLayoutStore, useInitiativeDataStore } from 'utilities/store';
import {
    useLabels,
    useUser,
    useContext,
    useModalState,
    useElseware,
    useInitiativeAndReportData,
    useConfig,
} from 'utilities/hooks';

// Components
import DropdownButton from 'components/dropdownButton';
import Button from 'components/button';
import Permission from 'components/permission';
import ConfirmModal from 'components/_modals/confirmModal';
import PdfModal from 'components/_modals/pdfModal';
import Report_4_0_PDF from 'components/_report/templates/report_4_0_pdf';
import Report_5_0_PDF from 'components/_report/templates/report_5_0_pdf';
import Dropdown from 'components/_assistedInteraction/dropdown';

// Icons
import {
    FiUser,
    FiChevronDown,
    FiMenu,
    FiEdit2,
    FiEye,
    FiX,
} from 'react-icons/fi';
import { LuSparkles } from 'react-icons/lu';

const HeaderComponent = ({ transparent, secondaryNavigation }) => {
    // ///////////////////
    // STORES
    // ///////////////////

    const {
        mainNavigationWide,
        mainNavigationActive,
        secondaryNavigationActive,
        setMainNavigationActive,
        setSecondaryNavigationActive,
    } = useLayoutStore();

    const { utilities } = useInitiativeDataStore();

    // ///////////////////
    // HOOKS
    // ///////////////////

    const { label } = useLabels();

    const router = useRouter();
    const { getUserLoggedIn, getUserName, userLogout, user } = useUser();

    const {
        INITIATIVE_ID,
        REPORT_ID,
        PORTFOLIO_ID,
        CONTEXT,
        CONTEXTS,
    } = useContext();

    const { getAssistedInteractionsForDropdown } = useConfig({
        initiativeType:
            utilities.initiative.get()?.Configuration_Type__c || null,
    });

    // ///////////////////
    // REFS
    // ///////////////////

    const userNavigationRef = useRef(null);

    // ///////////////////
    // STATE
    // ///////////////////

    const [userNavActive, setUserNavActive] = useState(false);
    const [
        assistedInteractionsActive,
        setAssistedInteractionsActive,
    ] = useState(false);

    // ///////////////////
    // METHODS
    // ///////////////////

    // Function: Event wrapper for closing outside click
    function handleClick(event) {
        if (
            userNavActive &&
            userNavigationRef.current &&
            !userNavigationRef.current.contains(event.target)
        ) {
            setUserNavActive(false);
        }
    }

    // ///////////////////
    // DATA
    // ///////////////////

    const assistedInteractions = getAssistedInteractionsForDropdown();

    // ///////////////////
    // EFFECTS
    // ///////////////////

    useEffect(() => {
        const handleRouteChange = () => {
            setAssistedInteractionsActive(false);
        };

        router.events.on('routeChangeStart', handleRouteChange);
        return () => {
            router.events.off('routeChangeStart', handleRouteChange);
        };
    }, [router]);

    // Effect: Catch outside clicks and close
    useEffect(() => {
        document.addEventListener('click', handleClick, true);
        return () => {
            document.removeEventListener('click', handleClick, true);
        };
    }, [userNavActive]);

    // ///////////////////
    // RENDER
    // ///////////////////

    return getUserLoggedIn() && !transparent ? (
        <>
            <div
                className={cc([
                    'animate-fade-in fixed left-0 top-0 flex items-center w-full z-header h-header page-px print:hidden ',
                    {
                        transparent: transparent,
                        'bg-amber-10': !transparent,
                    },
                ])}>
                {/* Mobile navigation toggle */}
                <Button
                    variant="tertiary"
                    theme="blue"
                    icon={FiMenu}
                    iconPosition="center"
                    iconType="stroke"
                    className="!px-8 !ring-0"
                    action={() => {
                        setMainNavigationActive(true);
                        if (secondaryNavigation) {
                            setSecondaryNavigationActive(true);
                        } else {
                            setSecondaryNavigationActive(false);
                        }
                    }}
                />

                {/* Call to actions and user details */}
                <div className="flex items-center ml-auto space-x-48">
                    {/* Call to actions */}
                    <div className="flex items-center space-x-12">
                        {INITIATIVE_ID && REPORT_ID && <ReportActions />}
                        {PORTFOLIO_ID && CONTEXT === CONTEXTS.PORTFOLIO && (
                            <PortfolioActions />
                        )}
                    </div>
                    <div className="flex items-center space-x-16">
                        {/* AI */}
                        {assistedInteractions.length > 0 && (
                            <Button
                                theme="amber"
                                icon={
                                    assistedInteractionsActive
                                        ? FiX
                                        : LuSparkles
                                }
                                variant="secondary"
                                size="tiny"
                                action={() => {
                                    setAssistedInteractionsActive(
                                        !assistedInteractionsActive
                                    );
                                }}>
                                {label('AI_FG_Assistant')} (
                                {assistedInteractions.length})
                            </Button>
                        )}

                        {/* User menu */}
                        <Button
                            size="tiny"
                            theme="blue"
                            variant="secondary"
                            icon={FiUser}
                            iconType="stroke"
                            action={() => setUserNavActive(!userNavActive)}>
                            <div className="flex items-center space-x-2">
                                <span>{getUserName()}</span>
                                <FiChevronDown className="relative ml-2 -top-1 w-14 h-14" />
                            </div>
                        </Button>
                    </div>
                </div>
            </div>

            {/* Assisted Interactions */}
            <Dropdown {...{ active: assistedInteractionsActive }} />

            {/* User menu */}
            <AnimatePresence>
                {userNavActive && (
                    <motion.div
                        ref={userNavigationRef}
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{
                            y: 5,
                            opacity: 0,
                            transition: { duration: 0.2 },
                        }}
                        className="fixed bg-white p-16 rounded-8 t-caption right-0 page-mr flex flex-col space-y-16 min-w-[192px] z-logo print:hidden -mt-8 shadow-md text-blue-100">
                        <div className="flex items-center">
                            <div className="flex items-center justify-center w-32 h-32 mr-12 text-white rounded-full bg-blue-20">
                                <FiUser className="w-24 h-24 stroke-current" />
                            </div>
                            <div className="flex flex-col">
                                <span>{getUserName()}</span>
                                <span className="-mt-2 text-blue-60 t-footnote">
                                    {user.User_Account_Name__c ||
                                        (user.User_Account_Type__c ===
                                            'Super' &&
                                            user.User_Account_Type__c)}
                                </span>
                            </div>
                        </div>
                        <div className="border-t border-blue-20"></div>
                        <button
                            onClick={() => {
                                userLogout();
                            }}
                            className="flex items-center transition-default hover:text-blue-300">
                            {label('LogOut')}
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    ) : null;
};

const ReportActions = () => {
    // ///////////////////
    // STORES
    // ///////////////////

    const { utilities, CONSTANTS } = useInitiativeDataStore();

    // ///////////////////
    // HOOKS
    // ///////////////////

    const router = useRouter();
    const { label } = useLabels();
    const { REPORT_ID } = useContext();
    const { ewUpdate, ewCreate } = useElseware();
    const {
        modalState: completeModalState,
        modalOpen: completeModalOpen,
        modalClose: completeModalClose,
        modalSaving: completeModalSaving,
        modalNotSaving: completeModalNotSaving,
    } = useModalState();
    const {
        modalState: publishModalState,
        modalOpen: publishModalOpen,
        modalClose: publishModalClose,
        modalSaving: publishModalSaving,
        modalNotSaving: publishModalNotSaving,
    } = useModalState();
    const {
        modalState: pdfModalState,
        modalOpen: pdfModalOpen,
        modalClose: pdfModalClose,
    } = useModalState();

    const { reportVersion } = useInitiativeAndReportData();

    // ///////////////////
    // METHODS
    // ///////////////////

    async function reportInProgress() {
        try {
            if (
                currentReport.Status__c !== CONSTANTS.REPORTS.REPORT_IN_PROGRESS
            ) {
                const { data: reportData } = await ewUpdate(
                    'initiative-report/initiative-report',
                    REPORT_ID,
                    {
                        Status__c: CONSTANTS.REPORTS.REPORT_IN_PROGRESS,
                    }
                );

                // Update store
                utilities.updateInitiativeData('_reports', reportData);
            }

            // Change location
            router.push(
                `/report/${
                    utilities.initiative.get()?.Id
                }/report-start/${REPORT_ID}`
            );
        } catch (error) {
            console.warn(error);
        }
    }

    async function completeReport() {
        // Modal save button state
        completeModalSaving();
        try {
            const { data: reportData } = await ewUpdate(
                'initiative-report/initiative-report',
                REPORT_ID,
                {
                    Status__c: CONSTANTS.REPORTS.REPORT_IN_REVIEW,
                }
            );

            // Update store
            utilities.updateInitiativeData('_reports', reportData);

            // Close modal
            completeModalClose();

            // Modal save button state
            completeModalNotSaving();
        } catch (error) {
            // Modal save button state
            completeModalNotSaving();
            console.warn(error);
        }
    }

    async function publishReport() {
        // Modal save button state
        publishModalSaving();
        try {
            const { data: reportData } = await ewUpdate(
                'initiative-report/initiative-report',
                REPORT_ID,
                {
                    Status__c: CONSTANTS.REPORTS.REPORT_PUBLISHED,
                }
            );

            // Update store
            utilities.updateInitiativeData('_reports', reportData);

            // Close modal
            publishModalClose();

            // Modal save button state
            publishModalNotSaving();

            router.reload();
        } catch (error) {
            // Modal save button state
            publishModalNotSaving();
            console.warn(error);
        }
    }

    function getReportLabel() {
        switch (currentReport.Status__c) {
            case CONSTANTS.REPORTS.REPORT_NOT_STARTED:
                return 'ButtonStartReporting';

            case CONSTANTS.REPORTS.REPORT_IN_PROGRESS:
                return 'ButtonContinueReporting';

            case CONSTANTS.REPORTS.REPORT_IN_REVIEW:
                return 'ButtonViewReporting';

            default:
                return 'ButtonContinueReporting';
        }
    }

    function getIsReportComplete() {
        const allReports = [
            'Summary_Of_Activities__c',
            'Summary_Of_Challenges_And_Learnings__c',
        ];

        const endOfGrantReports = [
            'Project_Purpose__c',
            'Progress_Towards_Grant_Area_Themes__c',
            'Important_Results__c',
        ];

        const isEndOfGrantReport =
            currentReport.Report_Type__c === CONSTANTS.REPORT_TYPE.END_OF_GRANT;

        return [
            ...allReports,
            ...(isEndOfGrantReport ? endOfGrantReports : []),
        ].every(
            field =>
                utilities.reports.get(REPORT_ID)[field] !== null &&
                utilities.reports.get(REPORT_ID)[field] !== ''
        );
    }

    // ///////////////////
    // DATA
    // ///////////////////

    const currentReport = utilities.reports.get(REPORT_ID);

    // 50 characters of “Initiative name” +  “_“ +
    // “Reporttype”(Annual_Report/End_of_Grant_Report/Status_Report) + “_“ +
    // Report end date (31.12.2024)
    const pdfReportTitle = `${utilities.initiative
        .get()
        ?.Name?.substring(0, 50)}_${
        currentReport?.Report_Type__c
    }_${currentReport.Due_Date__c?.replace(/\./g, '')}`;

    // ///////////////////
    // RENDER
    // ///////////////////

    return (
        <>
            <Permission
                {...{
                    rules: ['grantee.admin', 'grantee.collaborator', 'super'],
                    additionalRules: [
                        currentReport.Status__c !==
                            CONSTANTS.REPORTS.REPORT_PUBLISHED,
                    ],
                }}>
                <Button
                    theme="blue"
                    variant="primary"
                    action={reportInProgress}>
                    {label(getReportLabel())}
                </Button>
            </Permission>
            {(reportVersion === 'default' ||
                reportVersion === '4.0' ||
                reportVersion === '5.0') && (
                <Button theme="blue" variant="tertiary" action={pdfModalOpen}>
                    {label('ButtonSaveAsPdf')}
                </Button>
            )}
            <Permission
                {...{
                    rules: ['grantee.admin', 'grantee.collaborator', 'super'],
                    additionalRules: [
                        currentReport.Status__c !==
                            CONSTANTS.REPORTS.REPORT_PUBLISHED,
                    ],
                }}>
                <Button
                    theme="blue"
                    variant="tertiary"
                    disabled={
                        currentReport.Status__c ===
                        CONSTANTS.REPORTS.REPORT_IN_REVIEW
                    }
                    action={() => {
                        completeModalOpen();
                        // If the report is not complete, scroll to id of ReportSummary
                        // to show the user where they need to fill in the report
                        if (!getIsReportComplete()) {
                            const reportSummary = document.getElementById(
                                'ReportSummary'
                            );
                            if (reportSummary) {
                                reportSummary.scrollIntoView({
                                    behavior: 'smooth',
                                    block: 'start',
                                });
                            }
                        }
                    }}>
                    {label('ButtonSubmit')}
                </Button>
            </Permission>
            <Permission
                {...{
                    rules: ['funder.admin', 'super'],
                    additionalRules: [
                        currentReport.Status__c ===
                            CONSTANTS.REPORTS.REPORT_IN_REVIEW,
                    ],
                }}>
                <Button
                    theme="blue"
                    variant="primary"
                    disabled={
                        currentReport.Status__c ===
                        CONSTANTS.REPORTS.REPORT_PUBLISHED
                    }
                    action={publishModalOpen}>
                    {label('ButtonPublishReport')}
                </Button>
            </Permission>
            <ConfirmModal
                {...{
                    onCancel() {
                        completeModalClose();
                    },
                    async onSave() {
                        await completeReport();
                    },
                    canConfirm: getIsReportComplete(),
                    saveText: label('ButtonSubmit'),
                    title: getIsReportComplete()
                        ? label('ModalReportCompleteHeader')
                        : label('ModalReportNotCompleteHeader'),
                    ...completeModalState,
                }}>
                {getIsReportComplete() ? (
                    <p className="text-teal-100 t-preamble">
                        {label('ModalReportCompleteBody')}
                    </p>
                ) : (
                    <p className="text-teal-100 t-preamble">
                        {label('ModalReportCompleteBodyNotComplete')}
                    </p>
                )}
            </ConfirmModal>
            <ConfirmModal
                {...{
                    onCancel() {
                        publishModalClose();
                    },
                    async onSave() {
                        await publishReport();
                    },
                    saveText: label('ButtonPublishReport'),
                    title: label('ModalReportPublishHeader'),
                    ...publishModalState,
                }}>
                <p className="t-preamble">{label('ModalReportPublishBody')}</p>
            </ConfirmModal>
            <PdfModal
                {...{
                    ...pdfModalState,
                    title: pdfReportTitle,
                    onCancel: pdfModalClose,
                }}>
                {currentReport.Report_Viewer_Version__c === 4.0 ? (
                    <Report_4_0_PDF />
                ) : (
                    <Report_5_0_PDF />
                )}
            </PdfModal>
        </>
    );
};

const PortfolioActions = () => {
    // ///////////////////
    // HOOKS
    // ///////////////////

    const router = useRouter();
    const { label } = useLabels();
    const { ACTION, ACTIONS } = useContext();

    // ///////////////////
    // DATA
    // ///////////////////

    const isViewing = ACTION === ACTIONS.VIEW;
    const isEditing = ACTION === ACTIONS.EDIT;

    // ///////////////////
    // RENDER
    // ///////////////////

    return (
        <DropdownButton
            variant="tertiary"
            label={
                <div className="flex space-x-8">
                    {isEditing && (
                        <>
                            <FiEdit2 />{' '}
                            <span>{label('PortfolioStateEditing')}</span>
                        </>
                    )}
                    {isViewing && (
                        <>
                            <FiEye />{' '}
                            <span>{label('PortfolioStateViewing')}</span>
                        </>
                    )}
                </div>
            }
            items={[
                ...(isEditing
                    ? [
                          {
                              label: (
                                  <div className="flex space-x-8">
                                      <FiEye />{' '}
                                      <span>{label('PortfolioStateView')}</span>
                                  </div>
                              ),
                              action() {
                                  router.push(
                                      `/portfolio/${router.query?.portfolioId}/`
                                  );
                              },
                          },
                      ]
                    : []),
                ...(isViewing
                    ? [
                          {
                              label: (
                                  <div className="flex space-x-8">
                                      <FiEdit2 />{' '}
                                      <span>{label('PortfolioStateEdit')}</span>
                                  </div>
                              ),
                              action() {
                                  router.push(
                                      `/portfolio/${router.query?.portfolioId}/edit`
                                  );
                              },
                          },
                      ]
                    : []),
            ]}
        />
    );
};

HeaderComponent.propTypes = {
    secondaryNavigation: t.bool,
    transparent: t.bool,
};

HeaderComponent.defaultProps = {
    secondaryNavigation: false,
    transparent: false,
};

export default HeaderComponent;
